import { Alert, Button, useMobileAndTablet } from '@sonnen/web-ui';
import { parseHtmlTags } from '@sonnen/web-utils';
import { useAppContext } from '../../AppContext/AppContext';
import { useTranslation } from '../../i18n/i18n';
import { useOfferContext } from '../../OfferContext/OfferContext';
import BatteryOptimizationOfferCard from '../../Pages/BatteryOptimization/BatteryOptimizationOfferCard';
import { ModuleName } from '../../types';
import { hasSonnenStromContract, moduleAvailable } from '../../utils/utils';
import { BatteryOptimizationCards } from '../BatteryOptimizationCards/BatteryOptimizationCards';
import { Overlay } from '../Overlay/Overlay';
import styles from './BatteryOptimizationOverlay.module.css';

const useBatteryCompatibility = () => {
  const { offer } = useOfferContext();
  const { selectedSite, user } = useAppContext();

  if (!user || !selectedSite) return false;

  const available = offer ? moduleAvailable(offer, ModuleName.PRICE_OPTIMIZATION) : false;
  const sonnenStromCustomer = hasSonnenStromContract(selectedSite);

  return user && !available && !sonnenStromCustomer;
};

const IncompatibleBatteryAlert = () => {
  const { t } = useTranslation();

  return (
    <Alert variant="warning" title={t('modules.unavailable.title')}>
      {parseHtmlTags(t('modules.unavailable.content'))}
    </Alert>
  );
};

const ChargerAlert = () => {
  const { t } = useTranslation();
  const { offer } = useOfferContext();

  if (!offer || !offer.energyDynamic) return null;

  const { siteHasCharger } = offer.energyDynamic;

  if (!siteHasCharger) return null;

  return (
    <Alert title={t(`modules.priceOptimizationSoonAvailableForChargers.title`)} variant="info">
      {parseHtmlTags(t(`modules.priceOptimizationSoonAvailableForChargers.content`))}
    </Alert>
  );
};

type BatteryOptimizationOverlayProps = {
  onClose: () => void;
};

export const BatteryOptimizationOverlay = ({ onClose }: BatteryOptimizationOverlayProps) => {
  const { t } = useTranslation();
  const mobileAndTablet = useMobileAndTablet();
  const showIncompatibleBatteryWarning = useBatteryCompatibility();

  return (
    <Overlay
      dataTestId="battery-optimization-overlay"
      ariaLabel={t('priceOptimizationOverlay.ariaLabel')}
      onClose={onClose}
    >
      <div className={styles.content}>
        <div className={styles.twoColumnLayout}>
          <div className={styles.leftColumn}>
            <div className={styles.cardsWrapper}>
              {showIncompatibleBatteryWarning && <IncompatibleBatteryAlert />}
              <ChargerAlert />
              <BatteryOptimizationCards />

              {mobileAndTablet && <BatteryOptimizationOfferCard />}
            </div>
          </div>
          <div className={styles.rightColumn}>
            {!mobileAndTablet && (
              <div className={styles.rightColumnCard}>
                <BatteryOptimizationOfferCard />
              </div>
            )}
            <div className={styles.buttonWrapper}>
              <Button
                label={t('priceOptimizationOverlay.gotit')}
                onClick={onClose}
                size="large"
                expanded
                dataTestId="gotit-button"
              />
            </div>
          </div>
        </div>
      </div>
    </Overlay>
  );
};
